import { Component, HostListener, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  appInit,
  logoutAction,
  saveToasterNotification,
} from './store/actions/misc.actions';
import { AppState } from './store';
import { combineLatest, Observable } from 'rxjs';
import {
  getCurrentGymCustomCardLength,
  getDefaultLocationId,
  getGymFeaturesSelector,
  getGymName,
  getGymPermissionsSelector,
  getProfile,
  getSmsNotificationsEnabled,
  getSmsNumber,
  getUsername,
  isImpersonated,
} from './store/selectors/user.selector';
import { MenuEntry } from './models/misc/menu-entry';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { Profile } from './models/user/profile';
import { Permission } from './models/user/permission';
import { appAdminMenu, gymMenu, vendorMenu } from './models/misc/menu-values';
import {
  CreateToasterError,
  ToasterNotification,
} from './models/toaster-notification';
import { MemberDetailsService } from './services/member-details.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BarcodeCheck } from './models/barcode-check';
import 'dayjs/locale/ro';
import * as dayjs from 'dayjs';
import { CheckInResponse } from './models/check-in-response';
import { GymService } from './services/gym.service';
import { LoginResponse } from './models/account/login-response';
import { AccountService } from './services/account.service';
import { TranslateService } from '@ngx-translate/core';
import { GymFeaturesI } from './models/gym-features';
import { TranslationEnum } from '../assets/i18n/translation-enum';
import { CognitoService } from './services/cognito.service';
import { MemberMembership } from './models/member-membership';
import { UnpaidInvoices } from './models/invoices/invoice';
import { SignalRService } from './services/signal-r.service';
import { ClarityService } from './services/clarity.service';

dayjs.locale('ro');

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  title = 'TopGymFE';
  profile$: Observable<Profile>;
  menuEntries$: Observable<MenuEntry[]>;
  username$: Observable<string>;
  showNotification = false;
  notification$: Observable<ToasterNotification>;
  locationId: string;
  showCheckIn = false;
  memberships: MemberMembership[];
  checkInResponse: CheckInResponse;
  gymName$: Observable<string>;
  smsNotificationsEnabled$: Observable<boolean>;
  smsNumber$: Observable<number>;
  gymCustomCardLength?: number;
  startBarcode = null;
  barcode = '';
  barCodeLastChar = ''; // 2nd reader type
  barcodeLength = 13;
  barcodeCheck = BarcodeCheck;
  impersonated$: Observable<boolean>;
  impersonated = false;
  isGym$: Observable<boolean>;
  modalMemberId: string;
  gymFeatures: GymFeaturesI;
  isRefreshInProgress: boolean;
  showUnpaidBanner = false;
  unpaidBannerDismissed = false;
  unpaidStatus: UnpaidInvoices;
  showUnpaidModal = false;
  gymSuspended = false;
  canViewInvoices = false;
  bodyScrollLockClass = 'unpaid-modal-open';
  private lastReminderSection: string;
  private lastScanAt = 0;
  /** A scan's navigation lands within this window; see reminderFollowsScan. */
  private readonly scanQuietMs = 3000;
  menuCollapsed = this.readMenuCollapsed();
  TranslationEnum = TranslationEnum;
  availableLanguages = [
    { name: 'English', code: 'en', flag: 'gb' },
    { name: 'Română', code: 'ro', flag: 'ro' },
  ];
  selectedLanguage: { name: string; code: string; flag: string };
  langStorageKey = 'pref_lang';

  footerMenu = [
    {
      label: 'FAQ',
      link: '',
    },
    {
      label: 'Termeni si conditii',
      link: '',
    },
    {
      label: 'Politica de confidentialitate',
      link: '',
    },
    {
      label: 'ANSPC',
      link: '',
    },
  ];

  public shouldHideMenu$: Observable<boolean>;

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (
      (event.key === '?' ||
        (event.key === '/' && this.barCodeLastChar === 'Shift')) &&
      (this.startBarcode === '%^' || this.startBarcode === '%') &&
      this.barcode.length === this.barcodeLength
    ) {
      this.processBarcode();
    } else if (
      this.gymCustomCardLength &&
      event.key === 'Enter' &&
      this.barcode.length >= this.gymCustomCardLength
    ) {
      this.barcode = this.barcode.substring(
        this.barcode.length - this.gymCustomCardLength
      );
      this.processBarcode();
    }
    this.readBarcode(event);
    if (event.key === 'Escape' && this.showCheckIn) {
      this.showCheckIn = false;
    }
  }

  constructor(
    private store: Store<AppState>,
    private memberDetailsService: MemberDetailsService,
    private router: Router,
    private route: ActivatedRoute,
    private gymService: GymService,
    private accountService: AccountService,
    private cognitoService: CognitoService,
    private translate: TranslateService,
    private signalRService: SignalRService,
    private clarityService: ClarityService
  ) {
    translate.setDefaultLang('ro');
    this.changeLanguage(localStorage.getItem(this.langStorageKey) || 'ro');
  }

  changeLanguage(code: string) {
    this.selectedLanguage = this.availableLanguages.find((e) => e.code == code);
    this.translate.use(code);
    localStorage.setItem(this.langStorageKey, code);
  }

  readBarcode(event: KeyboardEvent) {
    // We have 2 reader types that input special characters differently.
    // Reader type 1 inputs % key as %. Reader type 2 inputs % key as Shift and 5.

    if (
      event.key === '%' ||
      (event.key === '5' && this.barCodeLastChar === 'Shift')
    ) {
      this.startBarcode = '%';
    } else if (
      (event.key === '^' ||
        (event.key === '6' && this.barCodeLastChar === 'Shift')) &&
      this.startBarcode === '%'
    ) {
      this.startBarcode = '%^';
      this.barcode = '';
    } else if (
      !!event.key &&
      !isNaN(+event.key) &&
      +event.key >= 0 &&
      +event.key <= 9
    ) {
      this.barcode += event.key;
    }

    this.barCodeLastChar = event.key;
  }

  processBarcode() {
    if (
      !this.barcode ||
      (this.barcode.length !== this.gymCustomCardLength &&
        this.barcode.length > this.barcodeLength)
    ) {
      return;
    }
    this.showCheckIn = false;
    this.lastScanAt = Date.now();

    // Track barcode scan event
    this.clarityService.trackEvent('barcode_scanned', {
      barcodeLength: this.barcode.length,
      currentUrl: this.router.url,
    });

    this.signalRService.notifyBarcodeChange(this.locationId, this.barcode);

    if (this.router.url.includes('pos')) {
      this.router.navigate(['/gym/pos/'], {
        queryParams: { barcode: this.barcode },
        queryParamsHandling: 'merge',
      });
      this.startBarcode = null;
      this.barcode = '';
      return;
    } else if (this.router.url.includes('?changeCard')) {
      this.router.navigate(
        [this.router.routerState.snapshot.url.split('?')[0]],
        {
          queryParams: { barcode: this.barcode },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        }
      );

      this.startBarcode = null;
      this.barcode = '';
      return;
    } else if (this.router.url.includes('member-summary')) {
      this.startBarcode = null;
      this.barcode = '';
      return;
    }

    this.startBarcode = null;
    const barcode = this.barcode;
    this.memberDetailsService
      .checkInVerify({
        barcode: this.barcode,
        locationId: this.locationId,
        membershipId: null,
        memberId: null,
        hour: new Date().getHours(),
        minute: new Date().getMinutes(),
      })
      .subscribe((result: CheckInResponse) => {
        if (!result) {
          this.memberDetailsService.updateData.next(
            MemberDetailsService.invalidString
          );
          this.showToasterError();
          // Track failed check-in
          this.clarityService.trackEvent('checkin_failed', {
            reason: 'no_result',
            barcode: barcode,
          });
          return;
        }

        if (result.status === BarcodeCheck.EMPTY_BARCODE) {
          this.router.navigate(['/gym/members/'], {
            queryParams: { barcode },
            queryParamsHandling: 'merge',
          });
          return;
        }

        if (!result.memberId || !result.username) {
          this.showToasterError('Card invalid');
          // Track invalid card
          this.clarityService.trackEvent('checkin_failed', {
            reason: 'invalid_card',
            barcode: barcode,
          });
          return;
        }

        if (
          result?.status === BarcodeCheck.MULTIPLE_ACTIVE ||
          result?.status === BarcodeCheck.NO_MEMBERSHIP ||
          result?.status === BarcodeCheck.CHECKED_IN ||
          result?.status === BarcodeCheck.CHECKED_OUT ||
          result?.status === BarcodeCheck.OUTSIDE_HOURS ||
          result?.status === BarcodeCheck.SKIPPED
        ) {
          this.router.navigate([], { queryParams: { closeModal: true } });

          // Track check-in status
          this.clarityService.trackEvent('checkin_status', {
            status: result.status,
            memberId: result.memberId,
            username: result.username,
            showModal: this.gymFeatures.showMemberModal,
          });

          if (this.gymFeatures.showMemberModal) {
            this.showCheckIn = true;
            this.checkInResponse = result;
            this.modalMemberId = result.memberId;
            this.memberDetailsService.updateData.next(this.modalMemberId);
          } else {
            this.router.navigate(['/gym/members/' + result.memberId], {});
          }
        }
      });
    this.barcode = '';
  }

  showToasterError(message = 'An error occurred. Please try again') {
    this.store.dispatch(
      saveToasterNotification({
        toasterNotification: CreateToasterError(message),
      })
    );
  }

  ngOnInit(): void {
    this.store.dispatch(appInit());
    this.cognitoService.checkUserSession();
    this.cognitoService.setAccessTokenRefreshInterval();

    this.profile$ = this.store.select(getProfile);
    this.impersonated$ = this.store.select(isImpersonated);
    this.impersonated$.subscribe((value) => (this.impersonated = value));

    // Everyone attached to a gym is warned about unpaid invoices, but only the
    // users who can act on them are pointed at the invoice list.
    this.profile$
      .pipe(
        filter((profile) => !!profile?.gymId && this.isGymUser(profile)),
        take(1),
        switchMap((profile) => {
          this.canViewInvoices =
            profile.permissions?.includes(Permission.MANAGE_GYM) ||
            profile.permissions?.includes(Permission.MANAGE_INVOICES);
          return this.accountService.hasUnpaidInvoices(profile.gymId);
        })
      )
      .subscribe((result) => this.applyUnpaidStatus(result));

    // The reminder comes back on every page the user opens, as long as it is
    // still unpaid; dismissing it only silences it for the current page.
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => this.refreshUnpaidModal(event.url));

    this.menuEntries$ = combineLatest([
      this.store.select(getGymPermissionsSelector),
      this.profile$.pipe(
        filter((profile) => !!profile),
        map((profile) => profile.permissions)
      ),
    ]).pipe(
      map(([gymPerm, perm]) => {
        if (perm?.includes(Permission.MANAGE_APP)) {
          return appAdminMenu();
        } else if (perm?.includes(Permission.MANAGE_ORDERS_PROVIDER)) {
          return vendorMenu();
        }

        const menu = gymMenu();
        menu.forEach((item) => {
          item.visible =
            (!item?.gymPerm || gymPerm.includes(item.gymPerm)) &&
            (!item.userPerm ||
              item.userPerm.filter((permission) => perm.includes(permission))
                .length >= 1);
        });
        return menu?.filter((item) => item.visible);
      }),
      shareReplay(1)
    );

    this.isGym$ = this.profile$.pipe(
      map(
        (value) =>
          !value?.permissions?.includes(Permission.MANAGE_APP) &&
          !value?.permissions?.includes(Permission.MANAGE_ORDERS_PROVIDER)
      )
    );
    this.username$ = this.store.select(getUsername);
    this.smsNotificationsEnabled$ = this.store.select(
      getSmsNotificationsEnabled
    );
    this.gymName$ = this.store.select(getGymName);
    this.smsNumber$ = this.store.select(getSmsNumber);
    this.store.select(getDefaultLocationId).subscribe((id) => {
      this.locationId = id;

      if (!!id) {
        this.signalRService.startConnection(id);
      }
    });

    this.route.queryParams.subscribe((params) => {
      if (params['closeModal']) {
        this.router.navigate([], { queryParams: {} });
      }

      this.isRefreshInProgress = params['refreshInProgress'];
    });

    this.store
      .select(getGymFeaturesSelector)
      .subscribe((data: GymFeaturesI) => {
        this.gymFeatures = data;
      });

    this.store
      .select(getCurrentGymCustomCardLength)
      .subscribe((data) => (this.gymCustomCardLength = data));

    this.shouldHideMenu$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.url.includes('member-summary'))
    );

    // Initialize Microsoft Clarity tracking (must be at the end after all observables are set)
    this.initializeClarityTracking();
  }

  exitImpersonation() {
    this.gymService.exitImpersonation().subscribe((response: LoginResponse) => {
      this.accountService.processLoginResponse(response);
    });
  }

  changeModalState() {
    this.showCheckIn = !this.showCheckIn;
    if (this.showCheckIn) {
      this.memberDetailsService.updateData.next(this.modalMemberId);
    }
  }

  dismissUnpaidModal(): void {
    if (this.isUnpaidModalBlocking) {
      return;
    }

    this.showUnpaidModal = false;
    this.setBodyScrollLock(false);
  }

  goToInvoices(): void {
    this.showUnpaidModal = false;
    this.setBodyScrollLock(false);
    this.router.navigate(['/invoices']);
  }

  /**
   * Once the grace period is over the modal cannot be dismissed. App admins and
   * impersonated sessions are exempt so support can still reach the gym.
   */
  get isUnpaidModalBlocking(): boolean {
    return this.gymSuspended && !this.isPlatformSession;
  }

  private applyUnpaidStatus(status: UnpaidInvoices): void {
    this.unpaidStatus = status;
    this.showUnpaidBanner = !!status?.hasUnpaidInvoices;
    this.gymSuspended = !!status?.hasUnpaidInvoices && !!status?.isSuspended;
    this.refreshUnpaidModal(this.router.url);
  }

  private refreshUnpaidModal(url: string): void {
    if (
      !this.unpaidStatus?.hasUnpaidInvoices ||
      this.isModalSuppressedRoute(url)
    ) {
      this.showUnpaidModal = false;
      this.setBodyScrollLock(false);
      return;
    }

    // Only opening a different section brings a dismissed reminder back.
    // Moving around inside one - a member profile, the next member, the
    // query-param navigations the app does on the spot - is the same page as
    // far as the user is concerned, and a scan is a workflow step rather than
    // a page they chose to open.
    const section = this.reminderSection(url);
    const landedOnNewSection =
      section !== this.lastReminderSection && !this.reminderFollowsScan();

    if (this.isUnpaidModalBlocking || landedOnNewSection) {
      this.showUnpaidModal = true;
    }
    this.lastReminderSection = section;

    this.setBodyScrollLock(this.showUnpaidModal && this.isUnpaidModalBlocking);
  }

  /**
   * The first two URL segments: /gym/members/<id> and /gym/members are the
   * same section, so opening a profile does not count as a new page.
   */
  private reminderSection(url: string): string {
    const segments = url
      .split('?')[0]
      .split('#')[0]
      .split('/')
      .filter((segment) => !!segment);

    return '/' + segments.slice(0, 2).join('/');
  }

  /**
   * A scan navigates on the user's behalf, sometimes across sections (the
   * dashboard jumps straight to the member). Time-boxed rather than a flag so
   * a scan that never navigates cannot silence the next real page change.
   */
  private reminderFollowsScan(): boolean {
    return Date.now() - this.lastScanAt < this.scanQuietMs;
  }

  /**
   * The invoice list stays reachable so a suspended gym can still pay, and the
   * login and member-summary screens are never interrupted.
   */
  private isModalSuppressedRoute(url: string): boolean {
    return (
      url.startsWith('/invoices') ||
      url.startsWith('/account') ||
      url.includes('member-summary')
    );
  }

  private isGymUser(profile: Profile): boolean {
    return (
      !profile.permissions?.includes(Permission.MANAGE_APP) &&
      !profile.permissions?.includes(Permission.MANAGE_ORDERS_PROVIDER)
    );
  }

  private get isPlatformSession(): boolean {
    return this.impersonated;
  }

  private setBodyScrollLock(locked: boolean): void {
    if (locked) {
      document.body.classList.add(this.bodyScrollLockClass);
    } else {
      document.body.classList.remove(this.bodyScrollLockClass);
    }
  }

  logout(): void {
    this.setBodyScrollLock(false);
    this.store.dispatch(logoutAction());
  }

  toggleMenu(): void {
    this.menuCollapsed = !this.menuCollapsed;
    try {
      localStorage.setItem('menuCollapsed', JSON.stringify(this.menuCollapsed));
    } catch {
      // storage unavailable (private mode); keep the in-memory state only
    }
  }

  private readMenuCollapsed(): boolean {
    try {
      return localStorage.getItem('menuCollapsed') === 'true';
    } catch {
      return false;
    }
  }

  private initializeClarityTracking(): void {
    // Track user information when profile is available
    this.profile$.pipe(filter((profile) => !!profile)).subscribe((profile) => {
      if (profile.username) {
        this.clarityService.identify(
          profile.username,
          undefined,
          undefined,
          profile.username
        );
        this.clarityService.setCustomTags({
          userType: profile.permissions?.includes(Permission.MANAGE_APP)
            ? 'admin'
            : 'gym_user',
          hasPermissions: profile.permissions?.length > 0 ? 'yes' : 'no',
        });
      }
    });

    // Track gym information
    this.gymName$.pipe(filter((gymName) => !!gymName)).subscribe((gymName) => {
      this.clarityService.setCustomTags({
        gymName: gymName,
      });
    });

    // Track page navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.clarityService.trackPageView(event.url);
      });
  }
}
